import { useMemo, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface DataTableColumn<T> {
  id: string;
  /** Header content; pass `''` (plus `srHeader`) for an unlabeled actions column. */
  header: ReactNode;
  /** Accessible header text when `header` is decorative/empty. */
  srHeader?: string;
  cell: (row: T) => ReactNode;
  className?: string;
  headerClassName?: string;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  pageSize?: number;
  className?: string;
  onRowClick?: (row: T) => void;
  /** Stable key per row. Falls back to array index. */
  getRowId?: (row: T, index: number) => string;
  /** Rendered instead of the table when `data` is empty. */
  emptyState?: ReactNode;
}

/**
 * Mirrors the admin-ui `DataTable` props API (`columns`, `data`, `pageSize`,
 * `className`, `onRowClick`) with a plain semantic `<table>` instead of
 * TanStack — `@tanstack/react-table` is not a dependency of this app and the
 * P1 tables need nothing beyond sorting-free paging. Real `<th scope="col">`
 * headers, whole-row click targets when `onRowClick` is set (design §10/§11,
 * C1), and client-side paging (all P1 lists arrive unpaginated).
 */
export function DataTable<T>({
  columns,
  data,
  pageSize = 10,
  className,
  onRowClick,
  getRowId,
  emptyState,
}: DataTableProps<T>) {
  const [page, setPage] = useState(0);

  const pageCount = Math.max(1, Math.ceil(data.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const rows = useMemo(
    () => data.slice(currentPage * pageSize, currentPage * pageSize + pageSize),
    [data, currentPage, pageSize],
  );

  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50 text-left">
              {columns.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  className={cn(
                    'px-4 py-3 font-medium text-muted-foreground',
                    column.headerClassName,
                  )}
                >
                  {column.header}
                  {column.srHeader ? <span className="sr-only">{column.srHeader}</span> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const clickable = !!onRowClick;
              return (
                <tr
                  key={getRowId ? getRowId(row, index) : index}
                  onClick={clickable ? () => onRowClick?.(row) : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={
                    clickable
                      ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onRowClick?.(row);
                          }
                        }
                      : undefined
                  }
                  className={cn(
                    'border-b last:border-b-0',
                    clickable &&
                      'cursor-pointer transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none',
                  )}
                >
                  {columns.map((column) => (
                    <td key={column.id} className={cn('px-4 py-3 align-middle', column.className)}>
                      {column.cell(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pageCount > 1 ? (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {currentPage + 1} of {pageCount}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((value) => Math.max(0, value - 1))}
              disabled={currentPage === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))}
              disabled={currentPage >= pageCount - 1}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
